/**
 * Toast Notification System - Reusable JavaScript Library
 * 
 * Usage:
 * 1. Include the CSS: {{ 'toast-notifications.css' | asset_url | stylesheet_tag }}
 * 2. Include this JS: {{ 'toast-notifications.js' | asset_url | script_tag }}
 * 3. Initialize: const toast = new ToastNotification();
 * 4. Show toasts: toast.show('Message', 'type', duration);
 * 
 * Types: 'error', 'warning', 'success', 'info'
 * Duration: milliseconds (default: 5000)
 * 
 * Example:
 * toast.show('Item added to cart!', 'success');
 * toast.show('Out of stock!', 'error', 6000);
 */

class ToastNotification {
  constructor(options = {}) {
    this.container = null;
    this.toastCount = 0;
    this.defaultDuration = options.defaultDuration || 5000;
    this.maxToasts = options.maxToasts || 5;
    this.position = options.position || 'top-right'; // top-right, top-left, bottom-right, bottom-left
    
    this.init();
  }

  init() {
    // Create toast container if it doesn't exist
    if (!document.querySelector('.toast-container')) {
      this.container = document.createElement('div');
      this.container.className = `toast-container toast-container--${this.position}`;
      this.setContainerPosition();
      document.body.appendChild(this.container);
    } else {
      this.container = document.querySelector('.toast-container');
    }
  }

  setContainerPosition() {
    // Remove default positioning and set based on position option
    this.container.style.position = 'fixed';
    this.container.style.zIndex = '9999';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.gap = '10px';
    this.container.style.pointerEvents = 'none';

    switch (this.position) {
      case 'top-left':
        this.container.style.top = '20px';
        this.container.style.left = '20px';
        this.container.style.right = 'auto';
        this.container.style.bottom = 'auto';
        break;
      case 'bottom-right':
        this.container.style.bottom = '20px';
        this.container.style.right = '20px';
        this.container.style.top = 'auto';
        this.container.style.left = 'auto';
        this.container.style.flexDirection = 'column-reverse';
        break;
      case 'bottom-left':
        this.container.style.bottom = '20px';
        this.container.style.left = '20px';
        this.container.style.top = 'auto';
        this.container.style.right = 'auto';
        this.container.style.flexDirection = 'column-reverse';
        break;
      default: // top-right
        this.container.style.top = '20px';
        this.container.style.right = '20px';
        this.container.style.left = 'auto';
        this.container.style.bottom = 'auto';
    }
  }

  show(message, type = 'info', duration = null) {
    // Use provided duration or default
    duration = duration || this.defaultDuration;
    
    // Limit number of toasts
    this.limitToasts();
    
    const toastId = `toast-${++this.toastCount}`;
    
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.id = toastId;

    // Create icon based on type
    const icon = this.getIcon(type);

    // Get color for progress bar based on type
    const progressColor = this.getProgressColor(type);

    toast.innerHTML = `
      <div class="toast-content">
        ${icon}
        <div class="toast-message">${this.escapeHtml(message)}</div>
        <button class="toast-close" type="button" aria-label="Close notification">&times;</button>
      </div>
      <div class="toast-progress toast-progress-${type}" style="animation-duration: ${duration}ms; background: ${progressColor};"></div>
    `;

    // Add close button functionality
    const closeBtn = toast.querySelector('.toast-close');
    closeBtn.addEventListener('click', () => {
      this.hide(toastId);
    });

    // Add to container
    this.container.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.classList.add('toast-show');
    }, 10);

    // Auto hide after duration
    const hideTimeout = setTimeout(() => {
      this.hide(toastId);
    }, duration);

    // Store timeout and timing info for pause/resume functionality
    toast.dataset.timeout = hideTimeout;
    toast.dataset.startTime = Date.now();
    toast.dataset.duration = duration;
    toast.dataset.remainingTime = duration;

    // Add hover event listeners for pause/resume
    toast.addEventListener('mouseenter', () => {
      this.pauseToast(toastId);
    });

    toast.addEventListener('mouseleave', () => {
      this.resumeToast(toastId);
    });

    return toastId;
  }

  hide(toastId) {
    const toast = document.getElementById(toastId);
    if (!toast) return;

    // Clear auto-hide timeout
    if (toast.dataset.timeout) {
      clearTimeout(parseInt(toast.dataset.timeout));
    }

    // Animate out
    toast.classList.add('toast-hide');
    toast.classList.remove('toast-show');

    // Remove from DOM after animation
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }

  pauseToast(toastId) {
    const toast = document.getElementById(toastId);
    if (!toast || !toast.dataset.timeout) return;

    // Clear the current timeout
    clearTimeout(parseInt(toast.dataset.timeout));
    
    // Calculate remaining time
    const elapsed = Date.now() - parseInt(toast.dataset.startTime);
    const remainingTime = parseInt(toast.dataset.duration) - elapsed;
    toast.dataset.remainingTime = Math.max(0, remainingTime);

    // Mark as paused
    toast.dataset.paused = 'true';
  }

  resumeToast(toastId) {
    const toast = document.getElementById(toastId);
    if (!toast || toast.dataset.paused !== 'true') return;

    // Get remaining time
    const remainingTime = parseInt(toast.dataset.remainingTime);
    
    if (remainingTime > 0) {
      // Set new timeout for remaining time
      const newTimeout = setTimeout(() => {
        this.hide(toastId);
      }, remainingTime);

      // Update stored data
      toast.dataset.timeout = newTimeout;
      toast.dataset.startTime = Date.now();
      toast.dataset.duration = remainingTime;
    }

    // Mark as not paused
    toast.dataset.paused = 'false';
  }

  limitToasts() {
    const toasts = this.container.querySelectorAll('.toast');
    if (toasts.length >= this.maxToasts) {
      // Remove oldest toast
      const oldestToast = toasts[0];
      if (oldestToast) {
        this.hide(oldestToast.id);
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getIcon(type) {
    const icons = {
      error: `<svg class="toast-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" style="color: #ff4444;"/>
      </svg>`,
      warning: `<svg class="toast-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" style="color: #ff7100;"/>
      </svg>`,
      success: `<svg class="toast-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" style="color: #0009ff;"/>
      </svg>`,
      info: `<svg class="toast-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" style="color: #2196f3;"/>
      </svg>`
    };
    return icons[type] || icons.info;
  }

  getProgressColor(type) {
    const colors = {
      error: '#ff4444',
      warning: '#ff7100',
      success: '#4caf50',
      info: '#2196f3'
    };
    return colors[type] || colors.info;
  }

  // Utility methods for common use cases
  error(message, duration) {
    return this.show(message, 'error', duration);
  }

  warning(message, duration) {
    return this.show(message, 'warning', duration);
  }

  success(message, duration) {
    return this.show(message, 'success', duration);
  }

  info(message, duration) {
    return this.show(message, 'info', duration);
  }

  // Clear all toasts
  clearAll() {
    const toasts = this.container.querySelectorAll('.toast');
    toasts.forEach(toast => {
      this.hide(toast.id);
    });
  }
}

// Auto-initialize if not already done and attach to window for global access
if (typeof window !== 'undefined') {
  // Only create global instance if one doesn't exist
  if (!window.toast) {
    window.toast = new ToastNotification();
  }
  
  // Also expose the class for custom instances
  window.ToastNotification = ToastNotification;
}